import ActionButton, {ActionButtonStyle} from "../ActionButton";
import CloseX from "../icons/CloseX";
import cn from "../../lib/classnames";
import keyCodes from "../../constants/keyCodes";
import React from "react";
import ReactDOM from "react-dom";
import styles from "./Modal.module.css";
import withModalContext from "./withModalContext";
import {ModalContextType} from "./ModalContext";

const strings = {
    close: "Close",
    closeIcon: "+",
    continue: "Continue",
    cancel: "Cancel",
};

export interface Focusable {
    focus: () => void;
}

export type ModalProps<T> = {
    bodyClassName?: string;
    children: React.ReactNode;
    className?: string;
    focusFooterOnShow?: boolean;
    id: string;
    isOpen: boolean;
    label: string;
    onClose?: () => void;
    onCancelLabel?: string;
    onSubmit: () => void;
    onSubmitLabel?: string;
    onSubmitDescription?: string;
    onSubmitLinkTo?: string;
    showCancelButton?: boolean;
    isCancelButtonPrimary?: boolean;
    onCancel?: () => void;
    triggerElement: T | null;
} & ModalContextType;

const modalRoot = document.getElementById("modal-root");

function Modal<T extends Focusable>({
    focusFooterOnShow = true,
    showCancelButton = false,
    isCancelButtonPrimary = false,
    ...props
}: ModalProps<T>) {
    const firstButtonRef = React.useRef<HTMLButtonElement>(null);
    const cancelButtonRef = React.useRef<HTMLButtonElement>(null);

    const submitButtonRef = React.useRef<HTMLButtonElement>(null);
    React.useEffect(() => {
        if (focusFooterOnShow && props.isOpen && submitButtonRef.current) {
            submitButtonRef.current.focus();
        }
    }, [focusFooterOnShow, props.isOpen]);

    const {isOpen, openModal, closeModal, id} = props;
    React.useEffect(() => {
        if (isOpen) {
            openModal(id);
        } else {
            closeModal(id);
        }
    }, [isOpen, id, openModal, closeModal]);

    function focusFirst(e: React.KeyboardEvent) {
        if (firstButtonRef.current && !e.shiftKey && e.keyCode === keyCodes.Tab) {
            e.preventDefault();
            firstButtonRef.current.focus();
        }
    }

    function focusTrigger() {
        if (props.triggerElement) {
            props.triggerElement.focus();
        }
    }

    function onClose() {
        if (props.onClose) {
            props.onClose();
            focusTrigger();
        }
    }

    const bodyId = `${props.label}_modal-body`;

    if (modalRoot === null) {
        return null;
    }

    return ReactDOM.createPortal(
        <div
            className={props.isOpen ? undefined : styles.isClosed}
            aria-label={props.label}
            aria-describedby={bodyId}
            role="dialog"
            aria-modal="true"
        >
            <div role="presentation" className={styles.overlay} onClick={onClose} />
            <div className={styles.cardContainer}>
                <div className={cn(styles.modalCard, props.className)}>
                    {props.onClose && (
                        <div className={styles.headerContainer}>
                            <button
                                ref={firstButtonRef}
                                onClick={onClose}
                                className={styles.closeButton}
                                aria-label={strings.close}
                                onKeyDown={(e) => {
                                    if (e.shiftKey && e.keyCode === keyCodes.Tab) {
                                        if (showCancelButton && cancelButtonRef.current) {
                                            e.preventDefault();
                                            cancelButtonRef.current.focus();
                                        } else if (submitButtonRef.current) {
                                            e.preventDefault();
                                            submitButtonRef.current.focus();
                                        }
                                    }
                                }}
                            >
                                <CloseX aria-hidden={true} />
                            </button>
                        </div>
                    )}
                    <div id={bodyId} className={cn(styles.bodyContent, props.bodyClassName)}>
                        {props.children}
                    </div>
                    <div className={styles.footer}>
                        <ActionButton
                            ref={submitButtonRef}
                            label={props.onSubmitLabel ?? strings.continue}
                            description={props.onSubmitDescription}
                            onClick={() => {
                                props.onSubmit();
                                focusTrigger();
                            }}
                            onKeyDown={showCancelButton ? undefined : focusFirst}
                            linkTo={props.onSubmitLinkTo ? props.onSubmitLinkTo : undefined}
                        />
                        {showCancelButton ? (
                            <ActionButton
                                ref={cancelButtonRef}
                                label={props.onCancelLabel ?? strings.cancel}
                                onClick={props.onCancel ? props.onCancel : onClose}
                                style={
                                    isCancelButtonPrimary
                                        ? ActionButtonStyle.primary
                                        : ActionButtonStyle.outlined
                                }
                                onKeyDown={focusFirst}
                            />
                        ) : null}
                    </div>
                </div>
            </div>
        </div>,
        modalRoot,
    );
}

export default withModalContext(Modal);
